import { isToday as isTodayDate, startOfWeek } from "date-fns";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "../../../utils/types";
import { SchedulerStoreType } from "../../scheduler/store/types";
import { FilterData } from "../../scheduler/store/types";
import { getDayNumber } from "../../scheduler/ui/dialog/copy-shifts-dialog/utils";
import { FTShiftDetails, RequestType } from "../store/worker-scheduler-types";
import { TransitionDialogState } from "../types/transition-types";
export interface RootState {
	workerScheduler: {
		filterData: FilterData;
		fetchFTWorkerFilters: {
			status: string;
		};
		fetchFTWorkerShifts: {
			status: string;
		};
		ftWorkerShifts: any[];
		ftWorkerFlexShifts: any[];
		currentDate: string;
		ftWorkerWorkcenters: number[];
		ftWorkerShiftPatterns: number[];
		ftWorkerPrimaryJobs: number[];
		selectedLocation: number | null;
		searchedWorkers: any[];
		searchTerm: string;
		requestTypes: RequestType[];
		fetchRequestTypes: {
			status: string;
		};
		transitionDialog: TransitionDialogState;
		fetchWorkerCallOffs: {
			status: string;
			data: any[];
		};
		fetchFTWorkerShiftDetails: {
			status: string;
			data: FTShiftDetails[];
		};
		updateWorkerAvailabilityStatus: {
			status: string;
			errorMessage: string;
		};
	};
	supervisor: {
		schedulerData: {
			currentDate: string;
		};
	};
	user: {
		userData: {
			weekStartDay: string;
			id: number;
			attendanceManagement: boolean;
		};
	};
	fullTimeShifts: {
		selectedLocation: number | null;
	};
}

const useWorkerSchedulerData = () => {
	const filterData = useSelector(
		(state: RootState) => state.workerScheduler.filterData
	);

	const allLocations = useSelector(
		(state: RootState) => state.workerScheduler.filterData.allLocations
	);

	const fetchFTWorkerFiltersStatus = useSelector(
		(state: RootState) => state.workerScheduler.fetchFTWorkerFilters.status
	);

	// const isWeekend = (date: Date) => {
	// 	const returnValue = date.getDay() === 6 || date.getDay() === 0;
	// 	return returnValue;
	// };

	const isToday = (date: Date) => {
		return isTodayDate(date);
	};

	const currentDate = useSelector(
		(state: StoreType) => state.fullTimeShifts?.currentDate
	);

	const weekStartDay = useSelector(
		(state: RootState) => state.user?.userData?.weekStartDay
	);

	const startingDay = useMemo(
		() =>
			startOfWeek(new Date(new Date(currentDate).setHours(0, 0, 0, 0)), {
				weekStartsOn: getDayNumber(weekStartDay),
			}),
		[weekStartDay, currentDate]
	);

	const fetchFTWorkerShiftsStatus = useSelector(
		(state: RootState) => state.workerScheduler.fetchFTWorkerShifts.status
	);

	const ftWorkerShifts = useSelector(
		(state: RootState) => state.workerScheduler.ftWorkerShifts
	);

	const ftWorkerFlexShifts = useSelector(
		(state: RootState) => state.workerScheduler.ftWorkerFlexShifts
	);

	const ftWorkerWorkcenters = useSelector(
		(state: RootState) => state.workerScheduler.ftWorkerWorkcenters
	);

	const ftWorkerShiftPatterns = useSelector(
		(state: RootState) => state.workerScheduler.ftWorkerShiftPatterns
	);

	const ftWorkerPrimaryJobs = useSelector(
		(state: RootState) => state.workerScheduler.ftWorkerPrimaryJobs
	);

	const selectedLocation = useSelector(
		(state: RootState) => state.fullTimeShifts.selectedLocation
	);

	const allWorkers = useSelector(
		(state: RootState) => state.workerScheduler.searchedWorkers
	);

	const searchedTerm = useSelector(
		(state: RootState) => state.workerScheduler.searchTerm
	);

	const currentUserId = useSelector(
		(state: RootState) => state.user?.userData?.id
	);

	const requestTypes = useSelector(
		(state: RootState) => state.workerScheduler.requestTypes
	);

	const fetchRequestTypesStatus = useSelector(
		(state: RootState) => state.workerScheduler.fetchRequestTypes.status
	);

	const workerStatusTransitionDialog = useSelector(
		(state: RootState) => state.workerScheduler.transitionDialog
	);

	const fetchWorkerCallOffsStatus = useSelector(
		(state: RootState) => state.workerScheduler.fetchWorkerCallOffs.status
	);

	const workerCallOffs = useSelector(
		(state: RootState) => state.workerScheduler.fetchWorkerCallOffs.data
	);

	const fetchFTWorkerShiftDetailsStatus = useSelector(
		(state: RootState) => state.workerScheduler.fetchFTWorkerShiftDetails.status
	);

	const fetchFTWorkerShiftDetails = useSelector(
		(state: RootState) => state.workerScheduler.fetchFTWorkerShiftDetails.data
	);

	const isAttendanceManagementEnabled = useSelector(
		(state: RootState) => state.user.userData.attendanceManagement
	);

	const schedulingStatusesObj = useSelector(
		(state: RootState) => state.workerScheduler.filterData.schedulingStatusesObj
	);

	const updateAvailabilityStatusStatus = useSelector(
		(state: RootState) =>
			state.workerScheduler.updateWorkerAvailabilityStatus.status
	);

	const updateAvailabilityStatusErrorMessage = useSelector(
		(state: RootState) =>
			state.workerScheduler.updateWorkerAvailabilityStatus.errorMessage
	);

	return {
		allLocations,
		allWorkers,
		filterData,
		fetchFTWorkerFiltersStatus,
		fetchFTWorkerShiftsStatus,
		ftWorkerShifts,
		ftWorkerFlexShifts,
		isToday,
		currentDate,
		startingDay,
		ftWorkerWorkcenters,
		ftWorkerShiftPatterns,
		ftWorkerPrimaryJobs,
		selectedLocation,
		searchedTerm,
		currentUserId,
		requestTypes,
		fetchRequestTypesStatus,
		workerStatusTransitionDialog,
		fetchWorkerCallOffsStatus,
		workerCallOffs,
		fetchFTWorkerShiftDetailsStatus,
		fetchFTWorkerShiftDetails,
		isAttendanceManagementEnabled,
		schedulingStatusesObj,
		updateAvailabilityStatusStatus,
		updateAvailabilityStatusErrorMessage,
	};
};
export default useWorkerSchedulerData;
